
//* Importing React modules
import React, { useEffect, useState } from "react";

//* Importing Bootstrap components
import { Modal, Button, Container, Row, Col, Card } from "react-bootstrap";

//* Importing Material-UI components
import { CircularProgress } from "@material-ui/core";

//* Importing custom hooks
import { useQuestions } from "../../../../hooks/questions/useQuestions";

//? Importing types
import { QuestionDetails } from "../../../../hooks/questions/types/types";
import { Tag } from "../../../../hooks/tags/types/types";

//todo import map functions
import {
  mapEducationLevel,
  mapDifficultyLevel,
} from "../../../../utils/questions/questionsMap";

//* Importing  useSanitizeInput hook
import { useSanitizeInput } from "../../../../constants/configJodit";



/**
 * Props for the ModalManage component.
 *
 * @typedef {Object} ModalManageProps
 * @property {string | null} id - The unique identifier for the modal, can be null.
 * @property {string} token - The authentication token.
 * @property {boolean} showModalManage - Flag to control the visibility of the modal.
 * @property {Function} setShowModalManage - Function to toggle the visibility of the modal.
 */
type ModalManageProps = {
  id: string | null;
  token: string;
  showModalManage: boolean;
  setShowModalManage: () => void;
};

/**
 * ModalManage component is responsible for displaying and managing the details of a specific question.
 * It fetches the question data based on the provided `id` and `token` and displays it in a modal.
 * The modal includes details such as education level, difficulty, discipline, tags, and sub-questions.
 * 
 * @param {ModalManageProps} props - The properties passed to the component.
 * @param {string} props.id - The ID of the question to be managed.
 * @param {string} props.token - The authentication token for API requests.
 * @param {boolean} props.showModalManage - Flag to control the visibility of the modal.
 * @param {React.Dispatch<React.SetStateAction<boolean>>} props.setShowModalManage - Function to set the visibility of the modal.
 * 
 * @returns {JSX.Element} The rendered ModalManage component.
 * 
 * @component
 * 
 * @example
 * return (
 *   <ModalManage
 *     id="questionId"
 *     token="authToken"
 *     showModalManage={showModalManage}
 *     setShowModalManage={setShowModalManage}
 *   />
 * );
 */
const ModalManage: React.FC<ModalManageProps> = ({
  id,
  token,
  showModalManage,
  setShowModalManage,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [questionData, setQuestionData] = useState<QuestionDetails | null>(
    null
  );
  const [error, setError] = useState<string>("");

  const { sanitizeInput } = useSanitizeInput();

  const { getQuestionById } = useQuestions(token);

  useEffect(() => {
    const fetchQuestion = async () => {
      if (!id || !showModalManage) return;

      setLoading(true);
      setError("");

      try {
        const question = await getQuestionById(id);
        setQuestionData(question);
      } catch (err) {
        setError("Falha ao carregar os dados da questão");
      } finally {
        setLoading(false);
      }
    };

    fetchQuestion();
  }, [id, showModalManage, token, getQuestionById]);

  // Renderiza subquestões com HTML formatado
  const renderSubQuestions = () => {
    if (!questionData?.subQuestions) return null;

    return questionData.subQuestions.map((subQuestion, index) => (
      <Col xs={12} key={subQuestion.id}>
        <Card className="mb-3 border-0">
          <Card.Body>
            <Card.Title className="mb-3" style={{ fontSize: "1.1rem" }}>
              {`Questão ${index + 1}`}
            </Card.Title>

            {/* 
              Em vez de parsear para texto,
              vamos exibir o HTML sanitizado 
            */}
            <Card.Text>
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    sanitizeInput(String(subQuestion.statement) || "") ||
                    "Nenhum enunciado disponível.",
                }}
              />
            </Card.Text>

            {subQuestion.type.toLowerCase() === "objective" &&
              subQuestion.objectives && (
                <>
                  <hr style={{ opacity: 0.5 }} />
                  <div style={{ fontWeight: 500, marginBottom: "0.5rem" }}>
                    Alternativas
                  </div>
                  {subQuestion.objectives.map((obj, idx) => (
                    <div
                      key={obj.id}
                      style={{
                        padding: "0.5rem",
                        marginBottom: "0.5rem",
                        borderRadius: "0.25rem",
                        backgroundColor: obj.isCorrect
                          ? "rgba(40,167,69,0.15)"
                          : "#f1f1f1",
                      }}
                    >
                      <p style={{ margin: 0, fontSize: "0.9rem" }}>
                        <strong>{String.fromCharCode(97 + idx) + ")"}</strong>{" "}
                        <span
                          dangerouslySetInnerHTML={{
                            __html: sanitizeInput(String(obj.text) || ""),
                          }}
                        />
                      </p>
                    </div>
                  ))}
                </>
              )}

            <hr style={{ opacity: 0.5 }} />
            <div style={{ fontWeight: 500, marginBottom: "0.5rem" }}>
              Justificativa da Resposta
            </div>
            <Card.Text>
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    sanitizeInput(String(subQuestion.answerJustification) || "") ||
                    "Nenhuma justificativa disponível.",
                }}
              />
            </Card.Text>
          </Card.Body>
        </Card>
      </Col>
    ));
  };

  return (
    <Modal
      show={showModalManage}
      onHide={setShowModalManage}
      size="xl"
      centered
      dialogClassName="modal-dialog-centered modal-xl"
      onExited={() => {
        setQuestionData(null);
        setError("");
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Gerenciar Questão</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Container fluid>
          {loading ? (
            <div className="text-center py-4">
              <CircularProgress />
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          ) : questionData ? (
            <>
              {/* Detalhes da Questão */}
              <Row className="mb-4">
                <Col xs={12} md={8}>
                  <Card className="shadow-sm border-0">
                    <Card.Header>Detalhes da Questão</Card.Header>
                    <Card.Body>
                      <p className="mb-2">
                        <strong>Nível de Educação:</strong>{" "}
                        {mapEducationLevel(questionData.educationLevel || "")}
                      </p>
                      <p className="mb-2">
                        <strong>Dificuldade:</strong>{" "}
                        {mapDifficultyLevel(questionData.difficulty || "")}
                      </p>
                      <p className="mb-2">
                        <strong>Disciplina:</strong>{" "}
                        {questionData.discipline.name}
                      </p>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Exibição das Tags */}
                {questionData.tags && questionData.tags.length > 0 && (
                  <Col xs={12} md={4}>
                    <Card className="shadow-sm border-0">
                      <Card.Header>Tags</Card.Header>
                      <Card.Body className="d-flex flex-wrap">
                        {questionData.tags.map((tag: Tag) => (
                          <div
                            key={tag.id}
                            style={{
                              backgroundColor: tag.color,
                              color: "#fff",
                              marginRight: "8px",
                              marginBottom: "8px",
                              fontSize: "0.9rem",
                              padding: "0.5em 0.75em",
                              borderRadius: "0.25rem",
                              display: "inline-block",
                            }}
                          >
                            {tag.name}
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                  </Col>
                )}
              </Row>

              {/* Se tiver um enunciado principal e mais de uma subquestão */}
              <Row className="g-4">
                {questionData.subQuestions &&
                  questionData.subQuestions.length > 1 &&
                  questionData.statement && (
                    <Col xs={12}>
                      <Card className="shadow-sm border-0">
                        <Card.Body>
                          <Card.Title>Enunciado da Questão</Card.Title>
                          <Card.Text>
                            <div
                              dangerouslySetInnerHTML={{
                                __html:
                                  sanitizeInput(String(questionData.statement)) ||
                                  "Nenhum enunciado disponível.",
                              }}
                            />
                          </Card.Text>
                        </Card.Body>
                      </Card>
                    </Col>
                  )}

                {renderSubQuestions()}
              </Row>
            </>
          ) : (
            <div className="alert alert-info">Nenhum dado para exibir.</div>
          )}
        </Container>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline-secondary"
          size="lg"
          onClick={setShowModalManage}
        >
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalManage;
